import { ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Hard guard for Founder-Pilot / contact-sales surfaces.
 *
 * Allows:
 *  - Guests (anonymous visitors — they can still submit a contact-sales form)
 *  - Admins / super admins
 *  - Users who own or admin at least one organization
 *  - Users arriving with a valid `?org=<uuid>` invite param
 *
 * Denies independents (logged-in users with no org context) and redirects
 * them to the self-serve plan picker so they don't get stranded.
 */
export default function RequireOrgContext({
  children,
  redirectTo = "/pricing#coach",
}: {
  children: ReactNode;
  redirectTo?: string;
}) {
  const { user, hasRole, isLoading } = useAuth();
  const { search } = useLocation();
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    if (isLoading) return;
    // Guests are allowed — contact-sales flow accepts anonymous leads.
    if (!user) {
      setAllowed(true);
      setChecking(false);
      return;
    }
    // Admin bypass
    if (hasRole("admin")) {
      setAllowed(true);
      setChecking(false);
      return;
    }
    // Invite token bypass
    const orgParam = new URLSearchParams(search).get("org");
    if (orgParam) {
      setAllowed(true);
      setChecking(false);
      return;
    }
    // Org membership check
    (async () => {
      const { data } = await supabase
        .from("organization_members")
        .select("id")
        .eq("user_id", user.id)
        .in("role", ["owner", "admin"])
        .limit(1);
      const ok = (data ?? []).length > 0;
      if (!ok) {
        toast.info("Founder Pilot is for studios & agencies. Start with a standard plan.");
      }
      setAllowed(ok);
      setChecking(false);
    })();
  }, [user?.id, isLoading, search, hasRole]);

  if (isLoading || checking) {
    return (
      <div className="container py-12">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }
  if (!allowed) return <Navigate to={redirectTo} replace />;
  return <>{children}</>;
}